<script lang="ts">
    import * as Dialog from "$lib/components/ui/dialog/index.js";
    import Button from "@/lib/components/ui/button/button.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { addDays, addMonths, differenceInCalendarDays, format } from "date-fns";
    import { DatePicker } from "@/Common";
    import { LocalDate } from "@/time";
    import { daysOfWeekStrings, durationSelections, maxWeeklyHoursBounds, unlimitedWeeklyHours, type CalendarDefaults, type daysOfWeek, type ShortWeekDayProperties } from "@/Common/constants";
    import { getDayMedians, getDisplayMedians } from "@/Util/utils";
    import WeekAvailability from "./WeekAvailability.svelte";
    import { signed } from "@/PlanReport/format";

    /**
     * Adjusting the Estimated Frontier of a Goal, on the account setup wizard and the Program page: the user sets the frontier they
     * want, and changes the target date and availability until the days to the target date reach it, with a target date, or
     * more riding time or another riding day, that would reach it suggested. When the plan's Max Weekly Hours holds the frontier
     * down, raising it is suggested first, and then the availability. onApply puts the date, availability and Max Weekly Hours in
     * the form. assess: the frontier, in watts over today's focus power, that a target date, availability and Max Weekly Hours
     * reach, and when a frontier isn't reached, the earliest date it is (null: not within a year).
     */
    export let open = false
    export let frontier: number
    export let targetDate: string
    export let defaults: CalendarDefaults
    export let trainingMedians: any
    export let assess: (date: string, defaults: CalendarDefaults, frontier: number, maxWeeklyHours: number) => Promise<{ frontier: number, earliestDate: Date | null } | null>
    export let onApply: (date: string, defaults: CalendarDefaults, maxWeeklyHours: number) => void
    // The plan's Max Weekly Hours (unlimitedWeeklyHours: none, as on the wizard)
    export let maxWeeklyHours = unlimitedWeeklyHours
    // Target dates are less than this many months away
    export let maxMonths = 6

    let wantedInput = ""
    let date = ""
    let hours = unlimitedWeeklyHours
    let days: CalendarDefaults
    // Each riding day's duration (-1: the system decides) and minutes when the dialog opened, or when a suggestion added the
    // day: what time added to it is measured from, and what it goes back to when an Off day is suggested instead.
    let start: Partial<Record<ShortWeekDayProperties, { duration: number, minutes: number }>> = {}
    // Start from the form's each time the dialog opens
    let wasOpen = false
    $: if (open !== wasOpen) {
        wasOpen = open
        if (open) {
            // A frontier with no room for an increase starts at the least one
            wantedInput = String(Math.max(MIN_FRONTIER, frontier))
            date = targetDate
            hours = maxWeeklyHours
            reached = null
            earliestDate = null
            suggestion = null
            checked = null
            days = structuredClone(defaults)
            start = {}
            for (const [dow, , d] of daysOfWeekStrings) {
                if (days[d].availability) start[d] = { duration: days[d].duration, minutes: minutesOf(days, dow, d) }
            }
        }
    }
    const MIN_FRONTIER = 1
    const MAX_FRONTIER = 200
    $: wanted = Math.round(Number(wantedInput))
    $: valid = wantedInput !== "" && wanted >= MIN_FRONTIER && wanted <= MAX_FRONTIER
    // The + and - buttons: a watt more or less, from the frontier when the field isn't a valid one
    function stepWanted(delta: number) {
        const from = valid ? wanted : frontier
        wantedInput = String(Math.min(MAX_FRONTIER, Math.max(MIN_FRONTIER, from + delta)))
    }

    // A change that reaches the frontier by the date, or comes closer: Max Weekly Hours raised, time added to some riding days,
    // or an Off day to ride on (enough: whether it reaches it on its own), or none that does.
    type Suggestion =
        | { kind: "time", added: [string, number][], days: CalendarDefaults }
        | { kind: "day", d: ShortWeekDayProperties, label: string, minutes: number, wasReset: boolean, enough: boolean, days: CalendarDefaults }
        | { kind: "hours", hours: number, enough: boolean }
        | { kind: "none" }

    // What the dialog's date and availability reach, checked shortly after a change. Each check supersedes the ones before it.
    // checked: the frontier, date and days of the last check, whose explanations stay shown (dimmed) during the next one, so
    // the dialog keeps its size.
    let checking = false
    let reached: number | null = null
    let earliestDate: Date | null = null
    let suggestion: Suggestion | null = null
    let checked: { wanted: number, date: string, days: CalendarDefaults, hours: number } | null = null
    let check = 0
    let checkTimer: ReturnType<typeof setTimeout>
    $: if (open && days) scheduleCheck(wanted, date, hours, JSON.stringify(days))
    function scheduleCheck(..._: unknown[]) {
        clearTimeout(checkTimer)
        // A check under way is for the inputs before this change: it publishes nothing
        ++check
        checking = true
        checkTimer = setTimeout(runCheck, 400)
    }

    async function runCheck() {
        const request = ++check
        if (!valid) { checking = false; return }
        const result = await assess(date, days, wanted, hours).catch(() => null)
        if (request !== check) return
        let found: Suggestion | null | undefined = null
        if (result && Math.round(result.frontier) < wanted) {
            found = await suggestFor(result.frontier, () => request !== check)
            if (found === undefined || request !== check) return
        }
        reached = result ? Math.round(result.frontier) : null
        earliestDate = result?.earliestDate ?? null
        suggestion = found
        checked = { wanted, date, days: structuredClone(days), hours }
        checking = false
    }
    $: isReached = valid && reached != null && checked != null && reached >= checked.wanted
    // Explanations under the target date and availability, from the last check
    $: explained = valid && reached != null && checked != null

    // Time is added a step at a time, a step being the next duration a day's availability offers (a quarter hour up to 4
    // hours, an hour past that) at least MIN_STEP more, each riding day to at most half an hour over its start. Past that
    // an Off day is suggested instead, or with no Off day left, up to 2 hours over.
    const ADD_LIMIT = 30
    const ADD_LIMIT_NO_DAY = 120
    const MIN_STEP = 10

    // The suggestion for the dialog's days, whose frontier is `base` (undefined: superseded by a later check). Max Weekly Hours
    // first, when it holds the frontier down: raised to reach it, or when the days fall short of it anyway, to what the days
    // reach without it, after which the next check suggests more availability.
    async function suggestFor(base: number, stale: () => boolean): Promise<Suggestion | undefined> {
        if (hours < maxWeeklyHoursBounds.max) {
            const free = (await assess(date, days, wanted, unlimitedWeeklyHours).catch(() => null))?.frontier
            if (stale()) return
            if (free != null && Math.round(free) > Math.round(base)) {
                const enough = Math.round(free) >= wanted
                const raised = await hoursFor(days, enough ? wanted : Math.round(free))
                if (stale()) return
                return { kind: "hours", hours: raised.hours, enough: enough && raised.enough }
            }
        }
        return suggest(base, hours, stale)
    }

    // The lowest Max Weekly Hours over the dialog's, in the slider's steps, at which the days reach `target` (enough: whether
    // one up to the slider's highest does; when none does, its highest)
    async function hoursFor(d: CalendarDefaults, target: number) {
        const { step, max } = maxWeeklyHoursBounds
        const steps: number[] = []
        for (let h = (Math.floor(hours/step) + 1)*step; h <= max; h += step) steps.push(h)
        const reaches = async (h: number) => Math.round((await assess(date, d, wanted, h).catch(() => null))?.frontier ?? -Infinity) >= target
        if (!steps.length || !(await reaches(steps[steps.length - 1]))) return { hours: max, enough: false }
        let lo = -1, hi = steps.length - 1
        while (hi - lo > 1) {
            const mid = Math.floor((lo + hi)/2)
            if (await reaches(steps[mid])) hi = mid; else lo = mid
        }
        return { hours: steps[hi], enough: true }
    }

    // The change to the availability for the dialog's days, whose frontier is `base`, with Max Weekly Hours of `weekHours`
    async function suggest(base: number, weekHours: number, stale: () => boolean): Promise<Suggestion | undefined> {
        const frontierOf = async (d: CalendarDefaults) => (await assess(date, d, wanted, weekHours).catch(() => null))?.frontier ?? -Infinity
        const reaches = (f: number) => Math.round(f) >= wanted
        const riding = daysOfWeekStrings.filter(([, , d]) => days[d].availability)
        const off = daysOfWeekStrings.filter(([, , d]) => !days[d].availability)
        const limit = off.length ? ADD_LIMIT : ADD_LIMIT_NO_DAY

        // The durations each riding day can step up to within its limit, the first step given first to the days it raises
        // the frontier most for each minute it adds (steps differ in size), then round them again: the fewest steps that
        // reach it. A day turned on in the dialog starts where it was turned on.
        const room: { d: ShortWeekDayProperties, now: number, steps: number[], gain: number }[] = []
        for (const [dow, , d] of riding) {
            const now = minutesOf(days, dow, d)
            start[d] = start[d] ?? { duration: days[d].duration, minutes: now }
            const steps = durationSelections.map(s => s.value).filter(v => v >= now + MIN_STEP && v <= start[d]!.minutes + limit)
            if (steps.length) room.push({ d, now, steps, gain: 0 })
        }
        const gains = await Promise.all(room.map(r => frontierOf(withDurations(days, { [r.d]: r.steps[0] }))))
        if (stale()) return
        room.forEach((r, i) => r.gain = (gains[i] - base)/(r.steps[0] - r.now))
        room.sort((a, b) => b.gain - a.gain)
        const order: ShortWeekDayProperties[] = []
        for (let round = 0; room.some(r => r.steps.length > round); ++round) {
            for (const r of room) if (r.steps.length > round) order.push(r.d)
        }
        // Each stepped day's duration after the first k steps, and the time it actually gains
        const stepsBy = (k: number) => {
            const taken: Partial<Record<ShortWeekDayProperties, number>> = {}
            for (const d of order.slice(0, k)) taken[d] = (taken[d] ?? 0) + 1
            return room.filter(r => taken[r.d]).map(r => ({ d: r.d, duration: r.steps[taken[r.d]! - 1], added: r.steps[taken[r.d]! - 1] - r.now }))
        }
        const durationsBy = (k: number) => Object.fromEntries(stepsBy(k).map(s => [s.d, s.duration])) as Partial<Record<ShortWeekDayProperties, number>>
        if (order.length && reaches(await frontierOf(withDurations(days, durationsBy(order.length))))) {
            if (stale()) return
            let lo = 0, hi = order.length
            while (hi - lo > 1) {
                const mid = Math.floor((lo + hi)/2)
                if (reaches(await frontierOf(withDurations(days, durationsBy(mid))))) hi = mid; else lo = mid
                if (stale()) return
            }
            const stepped = stepsBy(hi)
            return {
                kind: "time",
                added: daysOfWeekStrings.flatMap(([, , d]) => stepped.filter(s => s.d === d).map(s => [d, s.added] as [string, number])),
                days: withDurations(days, durationsBy(hi)),
            }
        }
        if (stale()) return
        if (!off.length) return { kind: "none" }

        // The Off day that raises the frontier most, at the rider's usual time for it, with the riding days back to their start
        const reset = structuredClone(days)
        let wasReset = false
        for (const [dow, , d] of riding) {
            const s = start[d]
            if (s && minutesOf(days, dow, d) > s.minutes) {
                reset[d].duration = s.duration
                wasReset = true
            }
        }
        let best: { f: number, d: ShortWeekDayProperties, label: string, days: CalendarDefaults } | null = null
        for (const [dow, label, d] of off) {
            const withDay = structuredClone(reset)
            withDay[d] = { ...withDay[d], availability: true, duration: snap(Math.max(30, usualMinutes(dow))) }
            const f = await frontierOf(withDay)
            if (stale()) return
            if (!best || f > best.f) best = { f, d, label, days: withDay }
        }
        return { kind: "day", d: best!.d, label: best!.label, minutes: best!.days[best!.d].duration, wasReset, enough: reaches(best!.f), days: best!.days }
    }

    function applySuggestion(s: Suggestion) {
        if (s.kind === "none") return
        if (s.kind === "hours") { hours = s.hours; return }
        if (s.kind === "day") start[s.d] = { duration: s.minutes, minutes: s.minutes }
        days = s.days
    }

    // The rider's usual minutes on a weekday, and a day's riding minutes: as set, or the usual when the system decides
    function usualMinutes(dow: daysOfWeek) {
        return Math.round(getDisplayMedians(getDayMedians(trainingMedians, dow)).duration*60)
    }
    function minutesOf(days: CalendarDefaults, dow: daysOfWeek, d: ShortWeekDayProperties) {
        return days[d].duration > 0 ? days[d].duration : usualMinutes(dow)
    }
    // The shortest duration a day's availability offers of at least the minutes
    function snap(minutes: number) {
        return durationSelections.find(s => s.value >= minutes)?.value ?? durationSelections[durationSelections.length - 1].value
    }
    function withDurations(defaults: CalendarDefaults, durations: Partial<Record<ShortWeekDayProperties, number>>): CalendarDefaults {
        const more = structuredClone(defaults)
        for (const [, , d] of daysOfWeekStrings) {
            if (durations[d]) more[d].duration = durations[d]!
        }
        return more
    }

    // 15 min, 1 hr, 1 hr 30 min: short, so the suggestions fit in the space kept for them
    function minutesText(m: number) {
        const hours = Math.floor(m/60), rest = m % 60
        return [hours && `${hours} hr`, rest && `${rest} min`].filter(Boolean).join(" ")
    }
    // Monday, Monday and Friday, Monday, Wednesday and Friday
    const listText = (items: string[]) => items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}` : items[0]
    // 15 min more on Mon and Fri and 30 min more on Sat
    function addedText(added: [string, number][]) {
        const byMinutes = [...new Set(added.map(([, m]) => m))].sort((a, b) => a - b)
        return listText(byMinutes.map(m => `${minutesText(m)} more on ${listText(added.filter(([, am]) => am === m).map(([label]) => label))}`))
    }

    // 18 weeks, 10 days
    function untilText(d: string) {
        const n = differenceInCalendarDays(LocalDate.atNoon(d), new Date())
        return n >= 14 ? `${Math.floor(n/7)} weeks` : `${n} day${n > 1 ? "s" : ""}`
    }
    // 3 riding days, and when a suggestion raised Max Weekly Hours: 3 riding days, with Max Weekly Hours raised to 8 hr
    function weekText(days: CalendarDefaults, hours: number) {
        const riding = daysOfWeekStrings.filter(([, , d]) => days[d].availability).length
        return `${riding} riding day${riding > 1 ? "s" : ""}` + (hours !== maxWeeklyHours ? `, with Max Weekly Hours raised to ${hoursText(hours)}` : "")
    }
    // 8 hr, 7.5 hr
    const hoursText = (h: number) => `${h} hr`

    const minDate = LocalDate.format(addDays(new Date(), 1))
    $: maxDate = LocalDate.format(addDays(addMonths(new Date(), maxMonths), -1))
    $: suggestedDate = earliestDate && LocalDate.format(earliestDate) <= maxDate ? LocalDate.format(earliestDate) : null

    function apply() {
        onApply(date, days, hours)
        open = false
    }
</script>

<Dialog.Root bind:open portal="#shadcn-ui-dialog-target">
    <Dialog.Content class="max-w-lg" overlayClass="bg-black/50 backdrop-blur-none">
        <Dialog.Header>
            <Dialog.Title>Adjust your Estimated Frontier</Dialog.Title>
            <Dialog.Description>
                Set the frontier you would like, then change your target date or availability to reach it.
            </Dialog.Description>
        </Dialog.Header>
        <form class="flex flex-col gap-5 mt-2" on:submit|preventDefault={apply}>
            <div class="flex items-center gap-3">
                <Label class="mb-0 mr-auto" for="adjust-frontier">Frontier Improvement</Label>
                <Button type="button" size="icon" variant="outline" class="rounded-full" aria-label="Lower frontier" disabled={valid && wanted <= MIN_FRONTIER} on:click={() => stepWanted(-1)}>
                    <i class="fa-regular fa-minus"/>
                </Button>
                <Input id="adjust-frontier" class="w-16 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" type="number" inputmode="numeric" min={MIN_FRONTIER} max={MAX_FRONTIER} step="1" bind:value={wantedInput}/>
                <Button type="button" size="icon" variant="outline" class="rounded-full" aria-label="Raise frontier" disabled={valid && wanted >= MAX_FRONTIER} on:click={() => stepWanted(1)}>
                    <i class="fa-regular fa-plus"/>
                </Button>
                <span class="text-slate-500 text-sm">w</span>
                <span class="w-6 text-center text-xl" role="status" aria-live="polite">
                    {#if checking}
                        <i class="fa-regular fa-spinner-third fa-spin text-slate-400" aria-label="Checking"/>
                    {:else if isReached}
                        <i class="fa-solid fa-circle-check text-green-600" aria-label="Reached"/>
                    {:else}
                        <i class="fa-solid fa-circle-xmark text-red-600" aria-label="Not reached"/>
                    {/if}
                </span>
            </div>
            <p class="text-sm text-slate-600 -mt-2">
                {#if !valid}
                    The frontier is between +1w and +200w.
                {:else if checking}
                    Checking your target date and availability…
                {:else if reached == null}
                    Your frontier could not be estimated.
                {:else if isReached}
                    Your target date and availability reach it: your frontier with them is {signed(reached)}w.
                {:else}
                    Your frontier with this target date and availability is {signed(reached)}w.
                {/if}
            </p>

            <div class="flex flex-col gap-2">
                <div class="flex items-center gap-3">
                    <Label class="mb-0 mr-auto">Target Date</Label>
                    <DatePicker class="w-44 text-base" min={minDate} max={maxDate} bind:value={date}/>
                </div>
                <!-- Space kept for the explanation, so the dialog doesn't change size as the frontier does -->
                <div class="min-h-[2.5rem] flex items-center gap-2 text-sm text-slate-600 transition-opacity" class:opacity-50={checking}>
                    {#if explained && checked && isReached}
                        <i class="fa-regular fa-circle-info text-slate-400"/>
                        <span>{format(LocalDate.atNoon(checked.date), "d MMM yyyy")} is {untilText(checked.date)} away: enough time to reach it.</span>
                    {:else if explained}
                        <i class="fa-regular fa-lightbulb text-amber-500"/>
                        {#if suggestedDate}
                            <span class="mr-auto">A target date of {format(LocalDate.atNoon(suggestedDate), "d MMM yyyy")} reaches it.</span>
                            <Button type="button" size="sm" variant="outline" disabled={checking} on:click={() => date = suggestedDate ?? date}>Use this date</Button>
                        {:else}
                            <span>No target date in the next {maxMonths} months reaches it with this availability.</span>
                        {/if}
                    {/if}
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <div class="flex items-center">
                    <Label class="mb-0 mr-auto">Availability</Label>
                    <span class="text-xs text-slate-400">Tap a day to change it</span>
                </div>
                {#if days}
                    <WeekAvailability bind:defaults={days} {trainingMedians}/>
                {/if}
                <div class="min-h-[2.5rem] flex items-center gap-2 text-sm text-slate-600 transition-opacity" class:opacity-50={checking}>
                    {#if explained && checked && isReached}
                        <i class="fa-regular fa-circle-info text-slate-400"/>
                        <span>{weekText(checked.days, checked.hours)}: enough to reach it by this date.</span>
                    {:else if explained}
                        <i class="fa-regular fa-lightbulb text-amber-500"/>
                        {#if suggestion?.kind === "hours"}
                            <span class="mr-auto">Your Max Weekly Hours of {hoursText(checked?.hours ?? hours)} {suggestion.enough ? "is too low to reach it" : "holds your days back"}. Raise it to {hoursText(suggestion.hours)}?</span>
                            <Button type="button" size="sm" variant="outline" disabled={checking} on:click={() => suggestion && applySuggestion(suggestion)}>Raise to {hoursText(suggestion.hours)}</Button>
                        {:else if suggestion?.kind === "time"}
                            <span class="mr-auto">{addedText(suggestion.added)} reaches it by this date.</span>
                            <Button type="button" size="sm" variant="outline" disabled={checking} on:click={() => suggestion && applySuggestion(suggestion)}>Add time</Button>
                        {:else if suggestion?.kind === "day"}
                            <span class="mr-auto">
                                Riding on {suggestion.label} too ({minutesText(suggestion.minutes)}){suggestion.wasReset ? ", without the time added to your other days," : ""}
                                {suggestion.enough ? "reaches it by this date." : "comes closer. More time will still be needed."}
                            </span>
                            <Button type="button" size="sm" variant="outline" disabled={checking} on:click={() => suggestion && applySuggestion(suggestion)}>Add {suggestion.label}</Button>
                        {:else}
                            <span>Longer rides don't reach it by this date. Move the target date or choose a lower frontier.</span>
                        {/if}
                    {/if}
                </div>
            </div>

            <Dialog.Footer class="mt-2 flex gap-2 sm:flex-row-reverse sm:justify-start">
                <Button type="submit">Apply</Button>
                <Button type="button" variant="outline" on:click={() => open = false}>Cancel</Button>
            </Dialog.Footer>
        </form>
    </Dialog.Content>
</Dialog.Root>
